import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { api, fmtCurrency, fmtDate } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusPill } from '@/components/fintech/StatusPill';
import { Upload, Send, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const SAMPLE = 'name,account,currency,amount,purpose\nJohn Doe,US00000001,USD,100.00,Salary\nJane Smith,US00000002,EUR,50.00,Consulting\n';

export default function MassPaymentsPage() {
  const { company } = useOutletContext();
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [title, setTitle] = useState('');
  const [saveTpl, setSaveTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get('/b2b/mass-payments').then(r => setItems(r.data.items || []));
    api.get('/b2b/templates').then(r => setTemplates(r.data.items || []));
  };
  useEffect(() => { load(); }, []);

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/b2b/mass-payments/preview-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data); setErrors(r.data.errors || []);
      toast.success(`Parsed ${r.data.count} rows`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to parse'); }
  };

  const loadTemplate = (t) => {
    setPreview({ recipients: t.recipients, count: t.recipients.length, totals: t.recipients.reduce((acc, r) => ({ ...acc, [r.currency]: (acc[r.currency] || 0) + Number(r.amount) }), {}) });
    setErrors([]);
    setTitle(t.name);
  };

  const execute = async () => {
    if (!preview?.recipients?.length) return;
    if (errors.length) { toast.error('Fix errors first'); return; }
    setBusy(true);
    try {
      const r = await api.post('/b2b/mass-payments', {
        title, recipients: preview.recipients,
        save_as_template: saveTpl && !!tplName, template_name: tplName,
      });
      toast.success(`Batch executed: ${r.data.count} recipients`);
      setPreview(null); setTitle(''); setSaveTpl(false); setTplName('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'sample-recipients.csv'; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Mass payments</h1>
        <Button size="sm" variant="outline" onClick={downloadSample}>Download sample CSV</Button>
      </div>

      <Card className="bg-white"><CardContent className="p-6 space-y-4">
        <div><Label>Batch title (optional)</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. July salaries" /></div>
        <div>
          <Label>Recipients CSV *</Label>
          <div className="mt-1 rounded-xl border border-dashed p-4 text-sm">
            <input ref={fileRef} type="file" accept=".csv" data-testid="mass-payment-csv-input" onChange={uploadCsv} />
            <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">Columns: name, account, currency (USD/EUR/KMF/KZT), amount, purpose</p>
          </div>
        </div>

        {templates.length > 0 && (
          <div>
            <Label>Or use template</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {templates.map(t => <Button key={t.id} size="sm" variant="outline" onClick={() => loadTemplate(t)}>{t.name} ({t.recipients.length})</Button>)}
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-3" data-testid="mass-payment-preview">
            <div className="flex flex-wrap gap-2">
              {Object.entries(preview.totals).map(([c, v]) => (
                <div key={c} className="rounded-full border bg-[hsl(var(--brand-gold))]/10 border-[hsl(var(--brand-gold))]/40 px-3 py-1 text-sm"><strong>{c}</strong> {fmtCurrency(v, c)} ({preview.recipients.filter(r => r.currency === c).length})</div>
              ))}
            </div>
            {errors.length > 0 && <div className="rounded-xl border border-[hsl(0_60%_85%)] bg-[hsl(0_72%_95%)] p-3 text-xs"><strong>Errors ({errors.length}):</strong><ul className="list-disc list-inside">{errors.map((e, i) => <li key={`err-${i}-${String(e).slice(0,32)}`}>{e}</li>)}</ul></div>}
            <div className="overflow-x-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Account</th><th className="p-2 text-left">Cur.</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Purpose</th></tr></thead>
                <tbody>{preview.recipients.slice(0, 20).map((r, i) => (
                  <tr key={`${r.account || 'row'}-${i}`} className="border-t"><td className="p-2 text-xs">{i+1}</td><td className="p-2">{r.name}</td><td className="p-2 text-xs">{r.account}</td><td className="p-2">{r.currency}</td><td className="p-2 text-right tabular-nums">{fmtCurrency(r.amount, r.currency)}</td><td className="p-2 text-xs">{r.purpose}</td></tr>
                ))}</tbody>
              </table>
              {preview.recipients.length > 20 && <div className="p-2 text-center text-xs text-[hsl(var(--muted-foreground))]">+ {preview.recipients.length - 20} more rows</div>}
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={saveTpl} onCheckedChange={setSaveTpl} data-testid="mass-payment-save-template" />Save as template</label>
              {saveTpl && <Input placeholder="Template name" className="w-56" value={tplName} onChange={e => setTplName(e.target.value)} data-testid="mass-payment-template-name" />}
            </div>
            <Button onClick={execute} disabled={busy || errors.length > 0} data-testid="mass-payment-execute" className="bg-[hsl(var(--brand-navy))]"><Send className="h-4 w-4 mr-2" />Execute batch</Button>
          </div>
        )}
      </CardContent></Card>

      <Card className="bg-white overflow-hidden">
        <div className="p-4 border-b font-serif text-lg">History</div>
        {items.length === 0 ? <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No batches yet</div> : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Title</th><th className="p-3 text-right">Recipients</th><th className="p-3 text-left">Totals</th><th className="p-3 text-left">Status</th></tr></thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id} className="border-t">
                  <td className="p-3 text-xs">{fmtDate(b.created_at)}</td>
                  <td className="p-3">{b.title}</td>
                  <td className="p-3 text-right tabular-nums">{b.count}</td>
                  <td className="p-3 text-xs">{Object.entries(b.totals || {}).map(([c, v]) => <div key={c}>{fmtCurrency(v, c)}</div>)}</td>
                  <td className="p-3"><StatusPill status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
